import { formatInTimeZone } from 'date-fns-tz';
import type { IMatchScheduleItem, IScheduleConstraints, IScheduleDayConstraints } from '../../types/schedule';
import {
  addCalendarDays,
  getTournamentCalendarSpan,
  reanchorInstantToCalendarDate,
} from '../../utils/tournamentDateTime';
import { resolveTournamentDayForMatch, stripMatchToUnscheduled } from '../../pages/tourDetails/Schedule/components/schedule.helpers';

export type ScheduleDayMismatchInfo = {
  expectedFirstDay: string;
  actualFirstDay: string;
  expectedDayCount: number;
  actualDayCount: number;
};

export function detectScheduleTimezoneStale(
  constraints: IScheduleConstraints | null,
  timeZone: string
): boolean {
  return Boolean(constraints?.scheduleTimezone && constraints.scheduleTimezone !== timeZone);
}

export function resolveTournamentSpanSource(
  tournamentStartIso: string,
  tournamentEndIso: string,
  constraints: IScheduleConstraints | null,
  timeZone: string
): { startIso: string; endIso: string } {
  if (
    constraints?.tournamentStartTime &&
    constraints?.tournamentEndTime &&
    constraints.scheduleTimezone === timeZone
  ) {
    return {
      startIso: constraints.tournamentStartTime,
      endIso: constraints.tournamentEndTime,
    };
  }
  return { startIso: tournamentStartIso, endIso: tournamentEndIso };
}

export function getScheduleDayMismatch(
  tournamentStartIso: string,
  tournamentEndIso: string,
  constraints: IScheduleConstraints,
  timeZone: string
): ScheduleDayMismatchInfo | null {
  if (!constraints.days.length) return null;

  const { startIso, endIso } = resolveTournamentSpanSource(
    tournamentStartIso,
    tournamentEndIso,
    constraints,
    timeZone
  );
  const span = getTournamentCalendarSpan(startIso, endIso, timeZone);
  const actualFirstDay = formatInTimeZone(
    new Date(constraints.days[0].startTime),
    timeZone,
    'yyyy-MM-dd'
  );
  const dayCountMismatch = constraints.days.length !== span.dayCount;
  const firstDayMismatch = actualFirstDay !== span.startYmd;

  if (!dayCountMismatch && !firstDayMismatch) {
    for (let i = 0; i < constraints.days.length; i++) {
      const expected = addCalendarDays(span.startYmd, i);
      const actual = formatInTimeZone(
        new Date(constraints.days[i].startTime),
        timeZone,
        'yyyy-MM-dd'
      );
      if (expected !== actual) {
        return {
          expectedFirstDay: span.startYmd,
          actualFirstDay,
          expectedDayCount: span.dayCount,
          actualDayCount: constraints.days.length,
        };
      }
    }
    return null;
  }

  return {
    expectedFirstDay: span.startYmd,
    actualFirstDay,
    expectedDayCount: span.dayCount,
    actualDayCount: constraints.days.length,
  };
}

export function detectScheduleDayMismatch(
  tournamentStartIso: string,
  tournamentEndIso: string,
  constraints: IScheduleConstraints | null,
  timeZone: string
): boolean {
  if (!constraints?.days?.length || !tournamentStartIso || !tournamentEndIso) return false;
  return getScheduleDayMismatch(tournamentStartIso, tournamentEndIso, constraints, timeZone) != null;
}

function resizeDaysToSpan(
  days: IScheduleDayConstraints[],
  dayCount: number
): IScheduleDayConstraints[] {
  if (!days.length || dayCount < 1) return days;

  const resized = [...days];
  const template = days[days.length - 1];

  while (resized.length < dayCount) {
    const source = resized[resized.length - 1] ?? template;
    resized.push({
      ...source,
      dayKey: resized.length,
      classOptions: source.classOptions ?? [],
    });
  }

  return resized.slice(0, dayCount).map((day, index) => ({
    ...day,
    dayKey: index,
  }));
}

export function alignScheduleDaysToTournament(
  constraints: IScheduleConstraints,
  tournamentStartIso: string,
  tournamentEndIso: string,
  timeZone: string
): IScheduleConstraints {
  const { startIso, endIso } = resolveTournamentSpanSource(
    tournamentStartIso,
    tournamentEndIso,
    constraints,
    timeZone
  );
  const span = getTournamentCalendarSpan(startIso, endIso, timeZone);
  const resizedDays = resizeDaysToSpan(constraints.days, span.dayCount);

  const alignedDays = resizedDays.map((day, index) => {
    const expectedYmd = addCalendarDays(span.startYmd, index);
    const reanchor = (iso: string) => reanchorInstantToCalendarDate(iso, timeZone, expectedYmd);
    return {
      ...day,
      dayKey: index,
      startTime: reanchor(day.startTime),
      endTime: reanchor(day.endTime),
    };
  });

  return {
    ...constraints,
    scheduleTimezone: timeZone,
    tournamentStartTime: startIso,
    tournamentEndTime: endIso,
    days: alignedDays,
  };
}

export function alignMatchToTournamentDays(
  match: IMatchScheduleItem,
  constraints: IScheduleConstraints,
  alignedConstraints: IScheduleConstraints,
  timeZone: string,
  allMatches?: IMatchScheduleItem[]
): IMatchScheduleItem {
  if (!match.scheduleDate && !match.startDate) return match;

  const day = resolveTournamentDayForMatch(match, constraints, timeZone, allMatches);
  if (!day) return stripMatchToUnscheduled(match);

  const span = getTournamentCalendarSpan(
    alignedConstraints.tournamentStartTime,
    alignedConstraints.tournamentEndTime,
    timeZone
  );
  const expectedYmd = addCalendarDays(span.startYmd, day.dayKey);
  const reanchor = (value: string | null | undefined): string | null => {
    if (!value) return null;
    return reanchorInstantToCalendarDate(value, timeZone, expectedYmd);
  };

  return {
    ...match,
    scheduleDate: reanchor(match.scheduleDate ?? match.startDate ?? null),
    startDate: reanchor(match.startDate ?? match.scheduleDate ?? null),
    scheduleEndDate: reanchor(match.scheduleEndDate ?? null),
    endDate: match.endDate ? reanchor(match.endDate) ?? match.endDate : match.endDate,
  };
}

export function alignMatchesToTournamentDays(
  matches: IMatchScheduleItem[],
  constraints: IScheduleConstraints,
  alignedConstraints: IScheduleConstraints,
  timeZone: string
): IMatchScheduleItem[] {
  return matches.map((match) =>
    alignMatchToTournamentDays(match, constraints, alignedConstraints, timeZone, matches)
  );
}
